import { PageHeader } from "@/components/design";
import { formatCurrency, formatHours } from "@/features/dashboard/utils";
import { cn } from "@/lib/utils";
import { WorkerReviewStatusBadge } from "@/features/pay-periods/components/worker-review-status-badge";
import { useDemo } from "../demo-context";

export function DemoPayPeriodReviewPage() {
  const { state } = useDemo();
  const review = state.payPeriodReview;
  const { totals, approvalProgress } = review;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Pay Period Review"
        description={`${review.label} · ${approvalProgress.percentComplete}% approved · read-only preview`}
      />

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {[
          { label: "Regular hours", value: formatHours(totals.regularHours) },
          { label: "Overtime", value: formatHours(totals.overtimeHours) },
          { label: "Gross pay", value: formatCurrency(totals.grossPay) },
          { label: "Open exceptions", value: String(totals.openExceptions) },
        ].map((stat) => (
          <div key={stat.label} className="rounded-xl border bg-card-white p-4 shadow-[var(--shadow-card)]">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">{stat.label}</p>
            <p className="mt-1 text-2xl font-bold tabular-nums text-navy-deep">{stat.value}</p>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-xl border bg-card-white shadow-[var(--shadow-card)]">
        <table className="w-full min-w-[900px] text-sm">
          <thead className="border-b bg-navy-deep/3 text-left text-[10px] uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="px-4 py-3 font-semibold">Employee</th>
              <th className="px-3 py-3 font-semibold">Site</th>
              <th className="px-3 py-3 font-semibold text-right">Reg</th>
              <th className="px-3 py-3 font-semibold text-right">OT</th>
              <th className="px-3 py-3 font-semibold text-right">Gross</th>
              <th className="px-3 py-3 font-semibold">Status</th>
              <th className="px-3 py-3 font-semibold text-right">Exc.</th>
            </tr>
          </thead>
          <tbody>
            {review.workers.map((worker) => (
              <tr
                key={worker.id}
                className={cn(
                  "border-b border-border/40",
                  worker.status === "EXCEPTION" && "bg-amber-50/40",
                  worker.status === "NEEDS_CORRECTION" && "bg-red-50/30",
                )}
              >
                <td className="px-4 py-3">
                  <p className="font-medium text-navy-deep">{worker.name}</p>
                  <p className="font-mono text-[10px] text-muted-foreground">{worker.employeeId}</p>
                </td>
                <td className="px-3 py-3 font-mono text-xs">{worker.siteCode}</td>
                <td className="px-3 py-3 text-right font-mono text-xs">{formatHours(worker.regularHours)}</td>
                <td className="px-3 py-3 text-right font-mono text-xs">{formatHours(worker.overtimeHours)}</td>
                <td className="px-3 py-3 text-right font-mono text-xs font-semibold">
                  {formatCurrency(worker.grossPay)}
                </td>
                <td className="px-3 py-3">
                  <WorkerReviewStatusBadge status={worker.status} />
                </td>
                <td className="px-3 py-3 text-right tabular-nums">{worker.openExceptions}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="rounded-xl border bg-card-white p-4 text-sm text-muted-foreground shadow-[var(--shadow-card)]">
        <p className="font-semibold text-navy-deep">Period lock workflow</p>
        <p className="mt-2">
          Review workers by site or crew, resolve exceptions, approve submitted entries, then lock
          the period for export. Ridgepoint&apos;s current period has {approvalProgress.workersApproved}{" "}
          of {approvalProgress.workersTotal} workers approved across {review.sites.length} sites.
        </p>
      </div>
    </div>
  );
}
